
import { BlockStore, CommonStore, FarmStore, JwtStore } from '@/store/modules';
import { QUploader } from 'quasar';
import { Component, Vue, Watch } from 'vue-property-decorator';
import QvSelect from '@/components/FormComponents/QvSelect.vue';
import QvInput from '@/components/FormComponents/QvInput.vue';
import { ValidationObserver } from 'vee-validate';
import { Action } from '@/store/api/actions';

@Component({
  components: {
    QvSelect,
    QvInput,
    ValidationObserver
  }
})
export default class PhotoForm extends Vue {
  $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
    uploader: InstanceType<typeof QUploader>;
  };

  maxFiles = 1000;
  totalFiles = 0;
  failedFiles = 0;
  cancelLabel = 'Close';
  uploadingFiles = false;

  currentFarm: null | number = null;
  currentCrop: null | number = null;
  date: string | null = null;
  dateVisible = false;

  get farms() {
    return FarmStore.Farms.map(x => {
      return { value: x.id, label: x.name };
    });
  }

  get crops() {
    const crops = BlockStore.SeasonCrops.map(x => {
      return {
        value: x.id,
        variety: x.variety,
        label: `${x.cropTypeName} - ${x.varietyName}`
      };
    });
    return crops;
  }

  get photoSizeLimit() {
    return 1024 * 1024 * 2;
  }

  get selectedFarmBlock() {
    return CommonStore.SelectedFarmBlock;
  }

  @Watch('selectedFarmBlock', { deep: true })
  onSelectedFarmBlockChange() {
    this.currentFarm = this.selectedFarmBlock.farmId;
    this.getCrops();
  }

  @Watch('failedFiles')
  onFailedFilesChanged(newVal: number) {
    if (newVal != 0 && this.totalFiles - newVal <= 0) {
      this.uploadingFiles = false;
    }
  }

  uploadProgressColor(status: string) {
    if (status == 'uploaded') {
      return 'positive';
    } else if (status == 'failed') {
      return 'negative';
    }
    return 'primary';
  }

  cancel() {
    this.$refs.uploader.abort();
    if (this.cancelLabel.toLowerCase() == 'close') {
      this.$router.push({ name: 'UploadPage' });
    }
  }

  byteToMB(size: number) {
    return size / (1024 * 1024);
  }

  // eslint-disable-next-line
  factory(files: any) {
    return new Promise((resolve, reject) => {
      for (const file of files) {
        resolve({
          method: 'POST',
          url: `/api/${Action.FruitCountImage}/`,
          headers: [
            {
              name: 'X-CSRFToken',
              value: this.$q.cookies.get('csrftoken')
            },
            {
              name: 'Authorization',
              value: `Bearer ${JwtStore.AccessToken}`
            }
          ],
          formFields: [
            { name: 'farm', value: this.currentFarm },
            { name: 'crop', value: this.currentCrop },
            { name: 'name', value: files[0].name },
            { name: 'date', value: this.date }
          ],
          fieldName: 'photo'
        });
      }
    });
  }

  factoryFailed(err: object, files: File[]) {
    //eslint-disable-next-line
    const uploader = this.$refs.uploader as any;
    const file = files[0];
    if (uploader.files) {
      const uploaderFiles = uploader.files.filter(
        //eslint-disable-next-line
        (x: any) => x.name == file.name && x.size == file.size
      );
      for (const uploderFile of uploaderFiles) {
        uploderFile.__message = err;
        uploderFile.__progress = 0.1;
      }
    }
  }

  // eslint-disable-next-line
  onRejected(rejectedEntries: any) {
    this.$q.notify({
      type: 'negative',
      message: `${rejectedEntries.length} file(s) did not pass validation constraints`
    });
  }

  // eslint-disable-next-line
  async afterUploaded(info: any) {
    this.totalFiles -= info.files.length;
    this.uploadingFiles = false;
    if (this.failedFiles > 0) {
      this.$q.notify({
        type: 'negative',
        message: `Failed to upload ${this.failedFiles} file(s)`
      });
    }
  }

  // eslint-disable-next-line
  uploading(info: any) {
    this.uploadingFiles = true;
  }

  // eslint-disable-next-line
  uploadingFailed(info: any) {
    //eslint-disable-next-line
    const uploader = this.$refs.uploader as any;
    const file = info.files[0];
    if (uploader.files) {
      const uploaderFiles = uploader.files.filter(
        //eslint-disable-next-line
        (x: any) => x.name == file.name && x.size == file.size
      );
      for (const uploderFile of uploaderFiles) {
        if (info.err && info.err.statusText) {
          uploderFile.__message = info.err.statusText;
        } else if (info.xhr && info.xhr.statusText) {
          uploderFile.__message = info.xhr.statusText;
          if (info.xhr.response) {
            const response = JSON.parse(info.xhr.response);
            if (response.message) {
              uploderFile.__messageDetail = response.message;
            }
          }
        }
      }
    }
    this.failedFiles += info.files.length;
  }

  // eslint-disable-next-line
  added(files: any) {
    this.totalFiles += files.length;
  }

  // eslint-disable-next-line
  removed(files: any) {
    this.totalFiles -= files.length;
  }

  pickFile() {
    this.$refs.uploader.pickFiles();
  }

  upload() {
    this.failedFiles = 0;

    const uploader = this.$refs.uploader as any;
    if (uploader.files) {
      for (const uploderFile of uploader.files) {
        uploderFile.__message = '';
        uploderFile.__messageDetail = '';
      }
    }
    this.$refs.observer.validate().then(() => {
      this.$refs.uploader.upload();
    });
  }

  abortUpload() {
    this.$refs.uploader.abort();
  }

  getCrops() {
    if (this.currentFarm) {
      BlockStore.getFarmAllCrops({ farmId: this.currentFarm });
    }
  }

  created() {
    FarmStore.getAllMyFarms();
    this.getCrops();
    this.onSelectedFarmBlockChange();
  }
}
